import { PublicOrderApiService } from '../api/services';
import { map, Observable, switchMap, tap } from 'rxjs';
import {
  PublicCheckoutOrderDetails,
  PublicCheckoutOrderPaymentMethod,
  PublicOrderBillingAddress,
  PublicOrderCollectionFilter,
  PublicOrderCollectionItem,
  PublicOrderDetails,
  PublicOrderInvoice,
  PublicOrderItem,
  PublicOrderItemAttribute,
  PublicOrderItemComponent,
  PublicOrderItemComponentAttribute,
  PublicOrderItemComponentGroup,
  PublicOrderPayment,
  PublicOrderShipment,
  PublicOrderShippingAddress,
  PublicOrderShippingAddressForm,
} from '../models';
import { blank, executeOrNull, Guid, valueOrDefault } from '@web/utils';
import { publicPaymentMethodPropertyFromResponse } from '../utils/public-payment-method-property-from-response';
import { inject, Injectable } from '@angular/core';
import {
  PublicOrderProcessRequest,
  PublicOrderShippingAddressUpdateRequest,
} from '../api/requests';
import {
  CollectionRequestBuilder,
  NavigatorService,
  YYYY_MM_DD_HH_MM_SS,
} from '@web/common';
import {
  DateFormatService,
  FileSaverService,
  InPostPointSelect,
  TableDataPayload,
  TableLazyLoad,
  ToastService,
} from '@web/shared/ui';
import { OrderNumber } from '../../shared/types';
import {
  dictionaryCountryFromResponse,
  phoneNumberWithPrefixFromResponse,
  phoneNumberWithPrefixToRequest,
} from '@web/shared/module/dictionary';
import { multimediaFromResponse } from '@web/shared/module/multimedia';
import {
  orderItemRefundSummaryFromResponse,
  orderRefundCollectionItemFromResponse,
  orderShipmentRefundSummaryFromResponse,
  refundSummaryFromResponse,
} from '../../shared/utils';

@Injectable({
  providedIn: 'root',
})
export class PublicOrderDataSourceService {
  private readonly fileSaver = inject(FileSaverService);
  private readonly dateFormat = inject(DateFormatService);
  private readonly orderApi = inject(PublicOrderApiService);
  private readonly navigator = inject(NavigatorService);
  private readonly collectionRequestBuilder = inject(CollectionRequestBuilder);
  private readonly toast = inject(ToastService);

  public fetchCollection(
    lazyLoad: TableLazyLoad<PublicOrderCollectionFilter>,
  ): Observable<TableDataPayload<PublicOrderCollectionItem>> {
    /* prettier-ignore */
    return this.orderApi
      .fetchCollection(
        {
          request: this.collectionRequestBuilder
            .page(lazyLoad.page)
            .pageSize(lazyLoad.rows)
            .sorts((builder) =>
              builder.fromTableSort(lazyLoad.sort).buildAsArray(),
            )
            .filters((builder) =>
              builder
                .fromTableFilter(lazyLoad.filter)
                .appendDate((builder) => builder.rangeFor('createdAt').build())
                .appendText((builder) => builder.caseInsensitiveLikeFor('number').build())
                .appendText((builder) => builder.equalFor('totalItems').build())
                .appendText((builder) => builder.inFor('status').build())
                .appendText((builder) => builder.caseInsensitiveLikeFor('totalPrice').build())
                .buildAsArray(),
            )
            .build()
        },
      )
      .pipe(
        map(
          (response) =>
            new TableDataPayload(
              response.items.map(
                (item) =>
                  /* prettier-ignore */
                  new PublicOrderCollectionItem({
                    canBeViewed: item.contextOperations.canBeViewed,
                    canShippingAddressBeUpdated: item.contextOperations.canShippingAddressBeUpdated,
                    createdAt: item.createdAt,
                    id: item.id,
                    number: item.number,
                    status: item.status,
                    totalItems: item.totalItems,
                    totalPrice: item.totalPrice,
                    refundSummary: executeOrNull(item.refundSummary, (summary) => refundSummaryFromResponse(summary))
                  }),
              ),
              response.pageSize,
              response.page,
              response.total,
            ),
        ),
      );
  }

  public process(options: {
    orderId: Guid;
    accessToken: string;
    secureFormToken?: string;
  }): Observable<void> {
    const { orderId, accessToken, secureFormToken } = options;

    /* prettier-ignore */
    return this.orderApi.process({
      orderId,
      accessToken,
      request: executeOrNull(secureFormToken, (token) => new PublicOrderProcessRequest({ secureFormToken: token })),
    }).pipe(
      tap(({redirectUrl}) => {
        this.navigator.navigateByExternalLink(redirectUrl);
      }),
      blank()
    );
  }

  public fetchCheckoutOrderDetailsById(options: {
    orderId: Guid;
    accessToken: string;
  }): Observable<PublicCheckoutOrderDetails> {
    const { orderId, accessToken } = options;
    return this.orderApi
      .fetchCheckoutOrderDetailsById({ orderId, accessToken })
      .pipe(
        map(
          (response) =>
            new PublicCheckoutOrderDetails({
              number: response.number,
              status: response.status,
              redirectUrl: response.redirectUrl,
              paymentMethod: new PublicCheckoutOrderPaymentMethod({
                code: response.paymentMethod.code,
                name: response.paymentMethod.name,
                description: response.paymentMethod.description,
                properties: response.paymentMethod.properties.map((property) =>
                  publicPaymentMethodPropertyFromResponse(property),
                ),
                id: response.paymentMethod.id,
              }),
              totalPrice: response.totalPrice,
            }),
        ),
      );
  }

  public redirect(params: {
    orderId: string;
    accessToken: string;
  }): Observable<{ number: string }> {
    const { orderId, accessToken } = params;

    return this.orderApi.redirect({ orderId, accessToken }).pipe(
      tap(({ redirectUrl }) => {
        this.navigator.navigateByExternalLink(redirectUrl);
      }),
      map(({ number }) => ({ number })),
    );
  }

  public fetchDetailsByNumber(
    number: OrderNumber,
  ): Observable<PublicOrderDetails> {
    return this.orderApi.fetchDetailsByNumber({ number }).pipe(
      map(
        (response) =>
          new PublicOrderDetails({
            id: response.id,
            number: response.number,
            status: response.status,
            totalPrice: response.totalPrice,
            refundSummary: executeOrNull(response.refundSummary, (summary) =>
              refundSummaryFromResponse(summary),
            ),
            totalItems: response.totalItems,
            createdAt: response.createdAt,
            comment: response.comment,
            billingAddress: new PublicOrderBillingAddress({
              city: response.billingAddress.city,
              dignity: response.billingAddress.dignity,
              email: response.billingAddress.email,
              firstName: response.billingAddress.firstName,
              lastName: response.billingAddress.lastName,
              phone: phoneNumberWithPrefixFromResponse(
                response.billingAddress.phone,
              ),
              postCode: response.billingAddress.postCode,
              streetLine1: response.billingAddress.streetLine1,
              country: dictionaryCountryFromResponse(
                response.billingAddress.country,
              ),
              streetLine2: response.billingAddress.streetLine2,
              state: response.billingAddress.state,
              companyName: response.billingAddress.companyName,
              companyNip: response.billingAddress.companyNip,
            }),
            items: response.items.map(
              (item) =>
                new PublicOrderItem({
                  name: item.name,
                  subName: item.subName,
                  slug: item.slug,
                  type: item.type,
                  quantity: item.quantity,
                  unitPrice: item.unitPrice,
                  price: item.price,
                  refundSummary: executeOrNull(item.refundSummary, (summary) =>
                    orderItemRefundSummaryFromResponse(summary),
                  ),
                  featuredFile: executeOrNull(item.featuredFile, (file) =>
                    multimediaFromResponse(file),
                  ),
                  attributes: valueOrDefault(item.attributes, []).map(
                    (attr) =>
                      new PublicOrderItemAttribute({
                        id: attr.id,
                        value: attr.value,
                        productAttributeId: attr.productAttribute.id,
                        productAttributeLabel: attr.productAttribute.label,
                      }),
                  ),
                  componentGroups: valueOrDefault(item.componentGroups, []).map(
                    (group) =>
                      new PublicOrderItemComponentGroup({
                        name: group.name,
                        isRandomSelected: group.isRandomSelected,
                        components: valueOrDefault(group.components, []).map(
                          (component) =>
                            new PublicOrderItemComponent({
                              name: component.name,
                              quantity: component.quantity,
                              featuredFile: executeOrNull(
                                component.featuredFile,
                                (file) => multimediaFromResponse(file),
                              ),
                              sourceId: component.source?.id,
                              sourceName: component.source?.name,
                              attributes: valueOrDefault(
                                component.attributes,
                                [],
                              ).map(
                                (attribute) =>
                                  new PublicOrderItemComponentAttribute({
                                    id: attribute.id,
                                    label: attribute.label,
                                    value: attribute.value,
                                  }),
                              ),
                            }),
                        ),
                      }),
                  ),
                }),
            ),
            payments: response.payments.map(
              (payment) =>
                new PublicOrderPayment({
                  number: payment.number,
                  status: payment.status,
                  createdAt: payment.createdAt,
                  paymentMethodCode: payment.method.code,
                  paymentMethodId: payment.method.id,
                  paymentMethodName: payment.method.name,
                }),
            ),
            shipments: valueOrDefault(response.shipments, []).map(
              (shipment) =>
                new PublicOrderShipment({
                  id: shipment.id,
                  canBeEdited: shipment.contextOperations.canBeEdited,
                  status: shipment.status,
                  price: shipment.price,
                  createdAt: shipment.createdAt,
                  methodId: shipment.method.id,
                  methodName: shipment.method.name,
                  methodCode: shipment.method.code,
                  scheduledShippingDate: shipment.scheduledShippingDate,
                  address: new PublicOrderShippingAddress({
                    city: shipment.address.city,
                    dignity: shipment.address.dignity,
                    email: shipment.address.email,
                    firstName: shipment.address.firstName,
                    lastName: shipment.address.lastName,
                    phone: phoneNumberWithPrefixFromResponse(
                      shipment.address.phone,
                    ),
                    postCode: shipment.address.postCode,
                    streetLine1: shipment.address.streetLine1,
                    country: dictionaryCountryFromResponse(
                      shipment.address.country,
                    ),
                    streetLine2: shipment.address.streetLine2,
                    state: shipment.address.state,
                    companyName: shipment.address.companyName,
                    shippingPoint: shipment.address.shippingPoint,
                    shippingPointName: shipment.address.shippingPointName,
                  }),
                  refundSummary: executeOrNull(
                    shipment.refundSummary,
                    (summary) =>
                      orderShipmentRefundSummaryFromResponse(summary),
                  ),
                  trackingUrl: shipment.trackingUrl,
                }),
            ),
            invoice: executeOrNull(
              response.invoice,
              (invoice) =>
                new PublicOrderInvoice({
                  number: invoice.number,
                  id: invoice.id,
                }),
            ),
            temporaryInvoice: executeOrNull(
              response.temporaryInvoice,
              (invoice) =>
                new PublicOrderInvoice({
                  number: invoice.number,
                  id: invoice.id,
                }),
            ),
            refunds: valueOrDefault(response.refunds, []).map((refund) =>
              orderRefundCollectionItemFromResponse(refund),
            ),
          }),
      ),
    );
  }

  public downloadInvoice(number: OrderNumber): Observable<void> {
    const invoiceFileName = `faktura_${number}_${this.dateFormat.format(new Date(), YYYY_MM_DD_HH_MM_SS)}`;

    return this.orderApi
      .downloadInvoice(number)
      .pipe(switchMap((blob) => this.fileSaver.saveAs(blob, invoiceFileName)));
  }

  public updateShippingAddressByFormData(param: {
    orderNumber: OrderNumber;
    shipmentId: Guid;
    data: PublicOrderShippingAddressForm;
  }): Observable<void> {
    const { orderNumber, shipmentId, data } = param;
    const request = new PublicOrderShippingAddressUpdateRequest({
      city: data.city!,
      companyName: data.companyName,
      countryId: data.countryId!,
      email: data.email!,
      firstName: data.firstName!,
      lastName: data.lastName!,
      phone: phoneNumberWithPrefixToRequest(data.phone!),
      postCode: data.postCode!,
      state: data.state,
      streetLine1: data.streetLine1!,
      streetLine2: data.streetLine2,
      shippingMethod: data.shippingMethodId,
      shippingPoint: executeOrNull(data.shippingPoint, (point) => point.name),
    });

    return this.orderApi
      .updateShippingAddress({ orderNumber, shipmentId, request })
      .pipe(
        tap(() => {
          this.toast.show((builder) =>
            builder
              .initSuccess(
                'Wybrana przesyłka dla zamówienia została zaktualizowana.',
              )
              .build(),
          );
        }),
      );
  }

  public fetchShippingAddressFormByNumber(params: {
    orderNumber: OrderNumber;
    shipmentId: Guid;
  }): Observable<PublicOrderShippingAddressForm> {
    const { orderNumber, shipmentId } = params;

    return this.orderApi
      .fetchShippingAddressDetails({ orderNumber, shipmentId })
      .pipe(
        map(
          (response) =>
            new PublicOrderShippingAddressForm({
              city: response.address.city,
              companyName: response.address.companyName,
              countryId: response.address.country?.id,
              email: response.address.email,
              firstName: response.address.firstName,
              lastName: response.address.lastName,
              phone: phoneNumberWithPrefixFromResponse(response.address.phone),
              postCode: response.address.postCode,
              state: response.address.state,
              streetLine1: response.address.streetLine1,
              streetLine2: response.address.streetLine2,
              shippingMethodId: response.method?.id,
              shippingPoint: executeOrNull(
                response.address.shippingPoint,
                (result) => {
                  return new InPostPointSelect({
                    description: result,
                  });
                },
              ),
            }),
        ),
      );
  }
}
